import { FaChartBar, FaUsers, FaBuilding, FaCheckCircle, FaTrophy, FaServer, FaBolt } from "react-icons/fa";

const WhyChooseUs = () => {
    const features = [
        {
            icon: <FaBuilding className="text-3xl" />,
            color: "blue",
            gradientFrom: "from-blue-500",
            gradientTo: "to-cyan-500",
            bgLight: "bg-blue-50",
            textColor: "text-blue-600",
            title: "Automated Intelligence",
            description: "AI-powered data collection that gathers comprehensive company information and real-time market insights automatically.",
            benefits: [
                "90% faster processing",
                "Real-time data updates",
                "Multi-source validation"
            ]
        },
        {
            icon: <FaChartBar className="text-3xl" />,
            color: "purple",
            gradientFrom: "from-purple-500",
            gradientTo: "to-pink-500",
            bgLight: "bg-purple-50",
            textColor: "text-purple-600",
            title: "Predictive Risk Analysis",
            description: "Advanced algorithms analyze location factors, industry trends, and market performance for precise risk assessment.",
            benefits: [
                "99.9% accuracy rate",
                "Predictive modeling",
                "Industry benchmarks"
            ]
        },
        {
            icon: <FaUsers className="text-3xl" />,
            color: "green",
            gradientFrom: "from-green-500",
            gradientTo: "to-teal-500",
            bgLight: "bg-green-50",
            textColor: "text-green-600",
            title: "Intuitive Platform",
            description: "Clean, modern interface designed for professionals. Complex analysis made simple and accessible.",
            benefits: [
                "5-minute setup",
                "No training required",
                "Mobile responsive"
            ]
        }
    ];

    return (
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-20">
                    <div className="inline-flex items-center justify-center p-2 bg-blue-100 rounded-full mb-4">
                        <FaTrophy className="text-blue-600 text-xl" />
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Why Industry Leaders Choose Us
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Cutting-edge technology meets practical business needs
                    </p>
                </div>

                {/* Feature Cards - No Images, Just Modern Cards */}
                <div className="grid lg:grid-cols-3 gap-8 mb-20">
                    {features.map((feature, index) => (
                        <div key={index} className="group relative">
                            {/* Gradient border effect */}
                            <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradientFrom} ${feature.gradientTo} rounded-2xl opacity-0 group-hover:opacity-100 transition duration-500 blur`}></div>
                            
                            {/* Card Content */}
                            <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                                {/* Icon */}
                                <div className={`${feature.bgLight} ${feature.textColor} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}>
                                    {feature.icon}
                                </div>
                                
                                {/* Title */}
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    {feature.title}
                                </h3>
                                
                                {/* Description */}
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    {feature.description}
                                </p>
                                
                                {/* Benefits List */}
                                <div className="space-y-3">
                                    {feature.benefits.map((benefit, idx) => (
                                        <div key={idx} className="flex items-center">
                                            <FaCheckCircle className={`${feature.textColor} mr-3 flex-shrink-0`} />
                                            <span className="text-sm text-gray-700">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Bottom accent */}
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradientFrom} ${feature.gradientTo} rounded-b-2xl`}></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Stats Section */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-12 text-white">
                    <h3 className="text-2xl font-semibold text-center mb-10 text-blue-100">
                        Trusted by Leading Insurance Companies and Brokers Across Europe
                    </h3>
                    <div className="grid md:grid-cols-4 gap-8 text-center">
                        <div className="group">
                            <FaUsers className="text-3xl text-blue-200 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                            <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform">10K+</div>
                            <div className="text-blue-100">Active Users</div>
                        </div>
                        <div className="group">
                            <FaChartBar className="text-3xl text-blue-200 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                            <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform">50M+</div>
                            <div className="text-blue-100">Data Points Analyzed</div>
                        </div>
                        <div className="group">
                            <FaServer className="text-3xl text-blue-200 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                            <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform">99.9%</div>
                            <div className="text-blue-100">Uptime SLA</div>
                        </div>
                        <div className="group">
                            <FaBolt className="text-3xl text-blue-200 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                            <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform">&lt; 3s</div>
                            <div className="text-blue-100">Average Response</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyChooseUs;